import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import "core-js/stable";
import { isEqual } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "regenerator-runtime/runtime";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { PaymentProcessor } from "../Account/BillingInformation/Shared/types/PaymentProcessorTypes";
import { AccountPageTitle } from "../Account/utils/Page";
import { RecipeIngredientReplacementMode } from "../OrderFlow/OrderFlowProvider/OrderFlowTypes";
import { NONE_INGREDIENT_ID } from "../OrderFlow/hooks/RecipeSelection/recipeSelectionUtils";
import { SubscriptionStatus } from "../OrderFlow/utils/orderFlowUtils";
import { useSkipDaysQuery } from "../SWR/SkipDays/useSkipDaysQuery";
import { ExtraSmallFab } from "../Shared/components/Buttons/ExtraSmallFAB";
import { convertToCartVisibilitySettings } from "../Shared/components/OrderSummary/OrderSummary.request";
import { AccountPageWrapper } from "../Shared/components/PageWrapper/AccountPageWrapper";
import { Prompt } from "../Shared/components/Prompt";
import { convertColors } from "../Shared/hooks/useConvertColors/useConvertColors";
import { LogLevel } from "../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../Shared/hooks/useLogging/useLogging";
import { ERROR_MESSAGE_GENERIC, ORDER_STATUS } from "../Shared/utils/constants";
import { createKey } from "../Shared/utils/createKey";
import { recursiveTokenFilter } from "../Shared/utils/tokenReplacement";
import { useScrollPosition } from "../hooks/useScrollPosition";
import { useMyMealsMenuContext } from "./MyMealsMenuContext/MyMealsMenuContext";
import { CartDrawer } from "./components/CartDrawer/CartDrawer";
import { MyMealsSkeleton } from "./components/MyMealsSkeleton";
import { PaymentDialog } from "./components/PaymentDialog/PaymentDialog";
import EditMealDialog from "./components/editMealDialog";
import FutureOrderDialog from "./components/futureOrderDialog";
import MealDaySection from "./components/mealDaySection";
import MealRowSection from "./components/mealRowSection";
import MealTabs from "./components/mealTabs";
import MealTabsSkeleton from "./components/mealTabsSkeleton";
import { isEmptyDay, toDayCostInfo } from "./hooks/useMealDay";
import { useMyMeals } from "./hooks/useMyMeals";
import { getWeekBreakdown, hasQuantityDiff, shapeRecipes } from "./utils/MyMeals-utils";
import deDupeWarningText from "./utils/deDupeWarningText";
import { getDefaultSelectedDay } from "./utils/getDefaultSelectedDay";
import getOriginalSelections from "./utils/getOriginalSelections";
import { getShouldShowPriceUpdates } from "./utils/getShouldShowPriceUpdates";
import "./utils/myMealsCSS.css";
import { areDayMealsModifiable } from "./utils/permissions";
import { proceedWithSaveChanges } from "./utils/proceedWithSaveChanges";
import {
  calculateTotal,
  createFutureOrder,
  getCustomerOrders,
  getDataModel,
  getFutureOrderDataModel,
  updateOrder,
} from "./utils/requests";

const noMenuSetMessage = "There is no longer a menu available for this week.";
const loadingText = "Preheating";

function handleNoMenuSetForCustomerOrders(setIsErrorState, setErrorMessage) {
  setIsErrorState(true);
  setErrorMessage(noMenuSetMessage);
}

/**
 * Returns the name and id of the order's recipe type or undefined if it doesn't have one.
 *
 * @param {*} selectedOrder
 * @param {*} selectedFutureOrder
 * @returns
 */
const getCurrentRecipeType = (selectedOrder, selectedFutureOrder) => {
  if (selectedFutureOrder && selectedFutureOrder.order) {
    if (selectedFutureOrder.order.RecipeTypeName && selectedFutureOrder.order.RecipeTypeId) {
      return {
        name: selectedFutureOrder.order.RecipeTypeName,
        id: selectedFutureOrder.order.RecipeTypeId,
      };
    }
  }

  if (selectedOrder && selectedOrder.order) {
    if (selectedOrder.order.RecipeTypeName && selectedOrder.order.RecipeTypeId) {
      return {
        name: selectedOrder.order.RecipeTypeName,
        id: selectedOrder.order.RecipeTypeId,
      };
    }
  }

  return undefined;
};

/**
 * This function modifies the days in place.
 * It sets the meal's OriginalQuantity to the current meal quantity and
 * sets the id for all None ingredient options
 *
 * @param {*} days
 */
const initializeMealAndIngredientData = (days) => {
  days.forEach((day) => {
    day.Meals.forEach((meal) => {
      meal.OriginalQuantity = meal.Quantity;
    });
    if (day.Recipes) {
      day.Recipes.forEach((recipe) => {
        if (recipe.RecipeIngredients) {
          recipe.RecipeIngredients.forEach((ingredient) => {
            if (ingredient.IngredientOptions) {
              ingredient.IngredientOptions.forEach((ingOption) => {
                const ingredientId =
                  ingOption?.IngredientId && ingOption.IngredientId !== 0 ? ingOption.IngredientId : NONE_INGREDIENT_ID;
                // None ingredient needs an id for the customization form to work
                ingOption.IngredientId = ingredientId;
              });
            }
          });
        }
      });
    }
  });
};

const MyMealsPage = () => {
  //* ===== SWR SKIP DAYS QUERY =====
  const { skippedDays, error: skipDaysError } = useSkipDaysQuery();

  const scrollPosition = useScrollPosition();
  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const location = useLocation();
  const navigate = useNavigate();
  const { datadogLog } = useLogging();
  const { selectedSubLinkId, setSelectedSubLinkId } = useMyMealsMenuContext(); // Access the selected sub-link context.

  // ===== LOADING STATES =====
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingFutureOrder, setIsLoadingFutureOrder] = useState(false);
  const [isUpdatingPrice, setIsUpdatingPrice] = useState(false);

  const [allowPastCutoff, setAllowPastCutoff] = useState(location.state && location.state.allowPastCutoff);
  const [futureOrderDates, setFutureOrderDates] = useState(location.state && location.state.futureOrderDates);
  const [days, setDays] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [orders, setOrders] = useState(null);
  const [config, setConfig] = useState(null);
  const [myMealsConfig, setMyMealsConfig] = useState(null);
  const [productAndRecipeCardLayoutSettings, setProductAndRecipeCardLayoutSettings] = useState(null);
  const [cartSettings, setCartSettings] = useState(null);
  const [symbolFlags, setSymbolFlags] = useState(null);
  const [recipeIngredientReplacementMode, setRecipeIngredientReplacementMode] = useState(
    RecipeIngredientReplacementMode.DISABLED
  );
  const [myMealsCompanyText, setMyMealsCompanyText] = useState(null);
  const [culture, setCulture] = useState(null);
  const [selectedMealQuantity, setSelectedMealQuantity] = useState(0);
  const [totalMealQuantity, setTotalMealQuantity] = useState(0);
  const [totalMealQuantityPerDay, setTotalMealQuantityPerDay] = useState({});
  const [isErrorState, setIsErrorState] = useState(false);
  const [errorMessage, setErrorMessage] = useState("An error has occurred.");
  const [chargeForUpdates, setChargeForUpdates] = useState(false);
  const [saveAndUpdateText, setSaveAndUpdateText] = useState("Save");
  const [quickSaveToolTipText, setQuickSaveToolTipText] = useState("Info");
  const [originalSelectionQuantity, setOriginalSelectionQuantity] = useState(null);
  const [originalRecipeSelection, setOriginalRecipeSelection] = useState(null);
  const [originalProductSelection, setOriginalProductSelection] = useState(null);
  const [cartIcon, setCartIcon] = useState(null);
  const [selectedOrderMealQuantity, setSelectedOrderMealQuantity] = useState(0);
  const [amountsPerDay, setAmountsPerDay] = useState({});
  const [amountForOrder, setAmountForOrder] = useState(0.0);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showSubscriptionWarningDialog, setShowSubscriptionWarningDialog] = useState(false);
  const [subscriptionWarningText, setSubscriptionWarningText] = useState("");
  const [subscriptionWarningTitle, setSubscriptionWarningTitle] = useState("");
  const [subscriptionWarningCallback, setSubscriptionWarningCallback] = useState(null);
  const [warningText, setWarningText] = useState("");
  const [totalQuantitiesPerDay, setTotalQuantitiesPerDay] = useState({});
  const [selectedFutureOrder, setSelectedFutureOrder] = useState({ order: null });
  const [futureOrders, setFutureOrders] = useState([]);
  const [isFutureOrderDialogOpen, setIsFutureOrderDialogOpen] = useState(false);
  const [futureOrderDialogText, setFutureOrderDialogText] = useState("");
  const [allowFutureOrderCreation, setAllowFutureOrderCreation] = useState(false);
  const [isEditMealDialogOpen, setIsEditMealDialogOpen] = useState(false);
  const [mealToEdit, setMealToEdit] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [navMenuLocationStateData, setNavMenuLocationStateData] = useState({
    orderNumber: undefined,
    allowPastcutoff: undefined,
    futureOrderDates: undefined,
    isMyMealsNavMenuSelection: false,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const orderNumber = searchParams.get("orderNumber") ?? location.state?.orderNumber;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [balance, setBalance] = useState(undefined);
  const [showQuickSaveButton, setShowQuickSaveButton] = useState(false);

  const {
    MyMealsLinkText,
    updateMealPropertiesPerDay,
    updateMealQuantity,
    handleRecipeToggle,
    updateRecipeQuantity,
    incrementRecipeQuantity,
    decrementRecipeQuantity,
    updateProductQuantity,
    updateCustomizationQuantity,
    handleCustomizeToggle,
    customizationSubmit,
  } = useMyMeals(
    myMealsConfig,
    selectedDay,
    setSelectedDay,
    days,
    setDays,
    amountForOrder,
    setAmountForOrder,
    amountsPerDay,
    setAmountsPerDay,
    totalQuantitiesPerDay,
    setTotalQuantitiesPerDay,
    setSelectedOrderMealQuantity,
    setTotalMealQuantityPerDay
  );

  useEffect(() => {
    setTimeout(() => {
      setIsScrolling(false);
    }, 1000);
  }, [isScrolling]);

  useEffect(() => {
    const handleScrolling = () => {
      setIsScrolling(true);
    };
    window.addEventListener("scroll", handleScrolling, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScrolling);
    };
  });

  /**
   * Filter out any days that are skipped by the customer
   */
  const filterSkipDays = (daysOnOrder, skippedDays) => {
    if (!daysOnOrder) return [];

    return daysOnOrder.filter((day) => {
      if (!skippedDays) return true;

      const foundSkipDay = skippedDays.find((skipDay) =>
        moment(skipDay.customerDate).utc().isSame(moment(day.DateFormatted).utc(), "day")
      );

      return !foundSkipDay;
    });
  };

  const handleShowErrorModal = (newWarningText) => {
    setShowErrorDialog(true);
    setWarningText(newWarningText);
  };

  const fetchOrders = useCallback((orderId = undefined) => {
    const onSuccess = (res) => {
      if (!res.data.success) {
        setIsErrorState(true);
        setErrorMessage("Something went wrong while looking for your orders.");
        return;
      }

      if (!res.data.orders || !res.data.orders.length) {
        setIsErrorState(true);
        setErrorMessage("No orders to display.");
        return;
      }

      setOrders(res.data.orders);
      if (orderId !== undefined) {
        const orderToBeLoaded = res.data.orders.find((order) => order.OrderId === orderId);
        if (!!orderToBeLoaded && orderToBeLoaded.IsOrderPastCutOff && !orderToBeLoaded.HasMenuSet) {
          handleNoMenuSetForCustomerOrders(setIsErrorState, setErrorMessage);
          return;
        }
        setSelectedOrder({ order: orderToBeLoaded });
      } else if (orderNumber) {
        const targetOrder = res.data.orders.find((order) => order.OrderNumber === orderNumber);
        if (!!targetOrder && targetOrder.IsOrderPastCutOff && !targetOrder.HasMenuSet) {
          handleNoMenuSetForCustomerOrders(setIsErrorState, setErrorMessage);
        }
        if (targetOrder && futureOrderDates) {
          // future order must be loaded after updateDataForOrder is completed in useEffect hook
          setSelectedOrder({ order: targetOrder });
          setIsLoadingFutureOrder(true);
        } else if (targetOrder) {
          setSelectedOrder({ order: targetOrder });
        } else {
          const defaultSelectedOrder = getDefaultSelectedOrder(res.data.orders);
          if (!defaultSelectedOrder && defaultSelectedOrder.IsOrderPastCutOff && !defaultSelectedOrder.HasMenuSet) {
            handleNoMenuSetForCustomerOrders(setIsErrorState, setErrorMessage);
          }
          setSelectedOrder({ order: defaultSelectedOrder });
        }
      } else {
        const defaultSelectedOrder = getDefaultSelectedOrder(res.data.orders);
        if (!!defaultSelectedOrder && defaultSelectedOrder.IsOrderPastCutOff && !defaultSelectedOrder.HasMenuSet) {
          handleNoMenuSetForCustomerOrders(setIsErrorState, setErrorMessage);
        }
        setSelectedOrder({ order: defaultSelectedOrder });
      }
      // TODO: update for R-14
      if (history && history.location && history.location.pathname) {
        navigate(history.location.pathname, { replace: true, state: undefined });
      }
    };

    const onError = () => {
      setIsErrorState(true);
      setErrorMessage("Something went wrong while looking for your orders.");
    };

    getCustomerOrders(onSuccess, onError);
  }, []);

  const handleEditMealClick = (meal) => () => {
    setMealToEdit(meal);
    setIsEditMealDialogOpen(true);
  };

  const handleEditMealDialogClose = (days) => {
    updateMealPropertiesPerDay(days);
    setMealToEdit(null);
    setIsEditMealDialogOpen(false);
  };

  const getFutureOrder = (orderNum, subId, start, end, previous, future, isUpcomingOrder = false) => {
    // This flag is to prevent recursive calls to getFutureOrderDataModel.
    let functionCalled = false;

    let onSuccess = (res) => {
      setIsLoadingFutureOrder(false);

      if (!res.data.success) {
        setFutureOrderDialogText(res.data.message);
        setIsFutureOrderDialogOpen(true);

        if (!res.data.hasFutureMenuSet) {
          // Check if we have an order for this start date and subscription if there's no future menu.
          const isCurrentOrder = orders.find(
            (order) => order.StartDate === start && order.SubscriptionLinkId === subId
          );
          if (isCurrentOrder) {
            setSelectedFutureOrder({ order: null });
            setSelectedOrder({ order: isCurrentOrder });
            setChargeForUpdates(isCurrentOrder.OrderStatus !== ORDER_STATUS.FUTURE);
            return;
          } else {
            // Try to get the last available future week if we don't have an order for this start date and subscription.
            if (!functionCalled) {
              getFutureOrderDataModel(orderNum, subId, start, end, false, false, onSuccess, onError);
              functionCalled = true;
            } else {
              // Show error and reload orders if we have already tried to get the last available future week.
              setIsLoadingFutureOrder(false);
              setFutureOrders([]);
              setSelectedFutureOrder({ order: null });
              setFutureOrderDialogText(noMenuSetMessage);
              setIsFutureOrderDialogOpen(true);
              fetchOrders();
              return;
            }
          }
        } else {
          setIsLoadingFutureOrder(false);
          setFutureOrders([]);
          setSelectedFutureOrder({ order: null });
          setFutureOrderDates(undefined);
          fetchOrders();
        }
        return;
      }

      if (!res.data.futureOrder.HasMenuSet) {
        setIsLoadingFutureOrder(false);
        setFutureOrders([]);
        setSelectedFutureOrder({ order: null });
        setFutureOrderDialogText(noMenuSetMessage);
        setIsFutureOrderDialogOpen(true);
        fetchOrders();
        return;
      }

      // if future order already exists, load its data instead of using sub template
      if (!isUpcomingOrder || res.data.futureOrder.OrderId || res.data.activeOrder) {
        const isCurrentOrder = orders.find(
          (order) =>
            order.SubscriptionLinkId === res.data.futureOrder.SubscriptionLinkId &&
            (order.StartDate === res.data.futureOrder.StartDate ||
              moment(order.ForWeekStartingOn).utc().format("MM/DD/YYYY") ==
                moment(res.data.futureOrder.StartDate).startOf("week").utc().format("MM/DD/YYYY"))
        );

        if (isCurrentOrder) {
          setFutureOrderDates(undefined);
          setSelectedFutureOrder({ order: null });
          setSelectedOrder({ order: isCurrentOrder });
          setChargeForUpdates(isCurrentOrder.OrderStatus !== ORDER_STATUS.FUTURE);
          return;
        } else if (res.data.activeOrder) {
          setFutureOrderDates(undefined);
          setSelectedFutureOrder({ order: null });
          setSelectedOrder({ order: res.data.activeOrder });
          setChargeForUpdates(res.data.activeOrder.OrderStatus !== ORDER_STATUS.FUTURE);

          let newOrders = orders.filter((o) => o.orderId != res.data.activeOrder.orderId);
          newOrders.push(res.data.activeOrder);
          setOrders(newOrders);
          return;
        }
      }

      let doesFutureOrderExist = futureOrders.find(
        (order) => JSON.stringify(order) === JSON.stringify(res.data.futureOrder)
      );

      if (!doesFutureOrderExist) {
        let fo = futureOrders;
        fo.push(res.data.futureOrder);
        setFutureOrders(fo);
      }

      initializeMealAndIngredientData(res.data.days);

      // Assignments have been added to maintain consistency with Order Flow until we have a definitive solution
      const days = res.data.days.map((day) => {
        return {
          ...day,
          Recipes: shapeRecipes(day.Recipes),
        };
      });

      setSelectedFutureOrder({ order: res.data.futureOrder });
      setChargeForUpdates(false);
      let defaultSelectedDay = getDefaultSelectedDay(days, skippedDays);
      setSelectedDay(defaultSelectedDay);
      setDays(days);
    };

    let onError = () => {
      setIsLoadingFutureOrder(false);
      setFutureOrders([]);
      setSelectedFutureOrder({ order: null });
      setFutureOrderDialogText("Error getting future order data.");
      setIsFutureOrderDialogOpen(true);
    };

    setDays(null);
    setSelectedDay(null);

    getFutureOrderDataModel(orderNum, subId, start, end, previous, future, onSuccess, onError);
  };

  let updateDataForOrder = useCallback(
    (callback = null) => {
      setDays(null);
      setSelectedDay(null);
      setIsDrawerOpen(false);
      setIsPaymentDialogOpen(false);
      setIsLoading(false);
      setTotalMealQuantity(0);
      setSelectedMealQuantity(0);

      if (selectedOrder == null) {
        return;
      }

      let onSuccess = (res) => {
        if (res.data.refetchOrders) {
          fetchOrders();
          return;
        }

        if (!res.data.success) {
          setIsErrorState(true);
          setErrorMessage(`Failed to retrieve data for order: ${selectedOrder.order.OrderNumber}`);
          return;
        }

        initializeMealAndIngredientData(res.data.days);

        if (res.data.futureOrder != null) {
          let doesFutureOrderExist = futureOrders.find(
            (order) => JSON.stringify(order) === JSON.stringify(res.data.futureOrder)
          );

          if (!doesFutureOrderExist) {
            let fo = futureOrders;
            fo.push(res.data.futureOrder);
            setFutureOrders(fo);
          }

          setSelectedFutureOrder({ order: res.data.futureOrder });
          setChargeForUpdates(false);
        }

        // Assignments have been added to maintain consistency with Order Flow until we have a definitive solution
        const days = res.data.days.map((day) => {
          return {
            ...day,
            Recipes: shapeRecipes(day.Recipes),
          };
        });

        setDays(days);
        const weekBreakdown = getWeekBreakdown(days);
        if (weekBreakdown.quantity) {
          setOriginalSelectionQuantity(weekBreakdown.quantity);
        }
        setConfig(res.data.config);
        setCartIcon(res.data.config.CartIcon);
        setCulture({ cultureInfo: res.data.config.CultureInfo, currency: res.data.config.Currency });
        setAllowFutureOrderCreation(res.data.config.CreateFutureOrders);

        const tokenMap = new Map();
        for (const key in res.data.config.TokenMap) {
          tokenMap.set(key, res.data.config.TokenMap[key]);
        }

        recursiveTokenFilter(res.data.config.MyMealsSettings, false, tokenMap);

        // We update the colors from the config for use in the price chip.
        // This ensures that the colors are in a format that MUI can use.
        const { ExtraRecipePriceColor, ReducedRecipePriceColor } = res?.data?.config?.MyMealsSettings ?? {};
        const updatedColors = convertColors(
          { ExtraRecipePriceColor, ReducedRecipePriceColor },
          theme.palette.common.black
        );

        const myMealsConfigWithUpdatedColors = {
          ...res.data.config.MyMealsSettings,
          ...updatedColors,
        };

        setMyMealsConfig(myMealsConfigWithUpdatedColors);
        setProductAndRecipeCardLayoutSettings(res.data.config.ProductAndRecipeCardLayoutSettings);
        setCartSettings(res.data.config.CartSettings);
        setSymbolFlags(res.data.config.SymbolFlags);
        setRecipeIngredientReplacementMode(res.data.config.RecipeIngredientReplacementMode);

        recursiveTokenFilter(res.data.config.CompanyText, false, tokenMap);

        setMyMealsCompanyText(
          res.data.config.CompanyText.filter(
            (text) => text.Section === "MyMealsPage" || text.Section === "RecipeControl"
          )
        );
        let { originalRecipes, originalProducts } = getOriginalSelections(days);
        setOriginalRecipeSelection(originalRecipes);
        setOriginalProductSelection(originalProducts);

        let defaultSelectedDay = getDefaultSelectedDay(days, skippedDays);
        setSelectedDay(defaultSelectedDay);

        if (
          selectedOrder.order.RecipeTypeId &&
          res.data.recipeTypes &&
          res.data.recipeTypes.some((rt) => rt.recipeTypeId == selectedOrder.order.RecipeTypeId)
        ) {
          let recipeTypeUpcharge = res.data.recipeTypes.find(
            (rt) => rt.recipeTypeId == selectedOrder.order.RecipeTypeId
          ).recipeTypePrice;
          days.forEach((day) => {
            day.Meals.forEach((meal) => {
              meal.Price += recipeTypeUpcharge;
            });
            day.Recipes.forEach((recipe) => {
              recipe.ProgramMealPrice += recipeTypeUpcharge;
            });
          });
        }
        setDays(days);
        updateMealPropertiesPerDay(days);

        if (callback && typeof callback == "function") {
          callback();
        }
      };

      let onError = () => {
        setIsErrorState(true);
        setErrorMessage(`Failed to retrieve data for order: ${selectedOrder.order.OrderNumber}`);
      };

      getDataModel(selectedOrder.order.OrderNumber, allowPastCutoff, onSuccess, onError);
      // Remove orderNumber from URL
      setSearchParams({}, { replace: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchOrders, selectedOrder]
  );

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    // future order must be loaded after getting data, otherwise order days will be reset back to not-future days.
    if (futureOrderDates) {
      updateDataForOrder(() => {
        getFutureOrder(
          selectedOrder.order.OrderNumber,
          selectedOrder.order.SubscriptionLinkId,
          futureOrderDates.start,
          futureOrderDates.end,
          false,
          false,
          true
        );
      });
    } else {
      updateDataForOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDataForOrder]);

  useEffect(() => {
    if (selectedOrder && config) {
      setChargeForUpdates(!config.SkipPayment && selectedOrder.order.OrderStatus === ORDER_STATUS.ACTIVE);
    }
  }, [selectedOrder, config]);

  const debouncedDays = useCalculationDebounce(
    days,
    setIsUpdatingPrice,
    chargeForUpdates || getShouldShowPriceUpdates(selectedOrder?.order),
    500
  );

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      if (debouncedDays && debouncedDays.length > 0) {
        let weekBreakdown = getWeekBreakdown(filterSkipDays(debouncedDays, skippedDays));
        setSelectedMealQuantity(weekBreakdown.quantity.recipe);
        setTotalMealQuantity(weekBreakdown.quantity.meal);

        // if a day is new and has no meals or products, it does not need to be calculated for
        // otherwise the customer will be charged shipping for nothing
        var dayCostInfos = debouncedDays
          .filter((day) => !isEmptyDay(day))
          .map((day) => {
            return toDayCostInfo(day, recipeIngredientReplacementMode);
          });

        let onCalculateTotalSuccess = (res) => {
          if (!res?.data?.success) {
            datadogLog("My Meals calculation failure", LogLevel.WARN, {
              orderId: selectedOrder.order.OrderId,
              days: debouncedDays,
            });
            setIsErrorState(true);
            setIsUpdatingPrice(false);
          }
          setBalance(res.data.balance);
          setIsUpdatingPrice(false);
        };

        let onCalculateTotalError = (e) => {
          datadogLog(
            "My Meals calculation error",
            LogLevel.WARN,
            { orderId: selectedOrder.order.OrderId, days: debouncedDays },
            e
          );
          setIsErrorState(true);
          setIsUpdatingPrice(false);
        };

        // Calculate the total for a pending order or when charging for updates.
        // Users are not charged for updates to pending orders, but we still need to calculate the total.

        if (chargeForUpdates || getShouldShowPriceUpdates(selectedOrder?.order)) {
          let model = {
            Days: dayCostInfos,
            OrderId: selectedOrder.order.OrderId,
            Order: selectedOrder.order,
          };

          calculateTotal(model, onCalculateTotalSuccess, onCalculateTotalError);
        } else {
          setIsUpdatingPrice(false);
        }
      }
    }

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDays, chargeForUpdates, selectedOrder]);

  const getDefaultSelectedOrder = (orders) => {
    const ordersNotPastCutOff = orders.filter((o) => !o.AllMealDaysPastCutOff);
    if (!ordersNotPastCutOff.length) {
      return orders[orders.length - 1];
    }
    const minOrderStartDate = moment.min(ordersNotPastCutOff.map((o) => moment(o.StartDate)));
    const minOrdersNotPastCutOff = ordersNotPastCutOff.filter((orderNotPastCutOff) =>
      moment(orderNotPastCutOff.StartDate).isSame(minOrderStartDate, "date")
    );
    return minOrdersNotPastCutOff[0];
  };

  const toggleDrawer = (open) => (event) => {
    if (event && event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
      return;
    }

    setIsDrawerOpen(open);
  };

  // If all days are past cutoff the save button should not display
  const allDaysPastCutoff = days && days.every((day) => day.IsDayPastCutOff);

  const haveProductsChangedFromOriginal = useCallback(() => {
    // If originalProductSelection is null, the fetch has not completed and we cannot compare.
    if (!days || allDaysPastCutoff || !originalProductSelection) {
      return false;
    }

    let daysWithMealsOrProducts = days.filter((day) => day.HasMealsOrProducts && !day.IsDayPastCutOff);
    let newProducts = [];
    daysWithMealsOrProducts.forEach((day) => {
      day.Products.forEach((product) => {
        if (product.NewQuantity > 0) {
          newProducts.push({
            mealDate: day.DateFormatted,
            productId: product.ProductId,
            purchasedQuantity: product.PurchasedQuantity,
            newQuantity: product.NewQuantity,
          });
        }
      });
    });

    let newProductsJson = JSON.stringify(newProducts);
    let originalProductsJson = JSON.stringify(originalProductSelection);

    return newProductsJson !== originalProductsJson;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalProductSelection, days]);

  const haveRecipesChangedFromOriginal = useCallback(() => {
    // If originalRecipeSelection is null, the fetch has not completed and we cannot compare.
    if (!days || allDaysPastCutoff || !originalRecipeSelection) {
      return false;
    }
    let daysWithMealsOrProducts = days.filter((day) => day.HasMealsOrProducts && !day.IsDayPastCutOff);
    let daysWithRecipes = daysWithMealsOrProducts.filter((day) => day.Recipes && day.Recipes.length > 0);

    let newRecipes = [];
    daysWithRecipes.forEach((day) => {
      day.Meals.forEach((meal) => {
        day.Recipes.forEach((recipe) => {
          if (recipe.Quantity > 0 && recipe.ProgramMealId === meal.ProgramMealId) {
            newRecipes.push({
              mealDate: day.DateFormatted,
              mealId: meal.ProgramMealId,
              recipeId: recipe.RecipeId,
              quantity: recipe.Quantity,
              ingReplacements: JSON.stringify(recipe.Assignments.map((a) => a.SelectedIngredientOptions)),
            });
          }
        });
      });
    });

    let newRecipesJson = JSON.stringify(newRecipes);
    let originalRecipesJson = JSON.stringify(originalRecipeSelection);

    return originalRecipesJson !== newRecipesJson;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalRecipeSelection, days]);

  const handleSkipDay = () => {
    selectedDay.Meals.forEach((meal) => {
      updateMealQuantity(meal.ProgramMealId, 0);
    });

    let newTotalQuantityPerDay = totalQuantitiesPerDay;
    delete newTotalQuantityPerDay[selectedDay.DateFormatted];
    setTotalQuantitiesPerDay(newTotalQuantityPerDay);

    selectedDay.Products.forEach((product) => {
      updateProductQuantity(product.ProductId, 0);
    });
  };

  const handleResponse = (res) => {
    if (res.data.success) {
      fetchOrders();
      updateDataForOrder();
    } else {
      setIsErrorState(true);
      setErrorMessage(res.data.message ?? ERROR_MESSAGE_GENERIC);
    }
  };

  const catchErrors = () => {
    console.error("MyMeals: Updating the future order response has failed.");
  };

  const saveAndUpdateCart = () => {
    let saveChangesStatus = proceedWithSaveChanges(
      myMealsConfig,
      filterSkipDays(days, skippedDays),
      totalMealQuantityPerDay,
      selectedOrderMealQuantity,
      amountsPerDay,
      amountForOrder,
      totalQuantitiesPerDay,
      selectedOrder.order.OrderType
    );

    if (!saveChangesStatus.proceed) {
      const deDupedWarningText = deDupeWarningText(saveChangesStatus.dayWarningMessages);
      handleShowErrorModal(deDupedWarningText);
      return;
    }

    if (
      config.SkipPayment ||
      selectedOrder.order.OrderStatus === ORDER_STATUS.PENDING ||
      balance?.remainingBalance <= 0
    ) {
      setIsLoading(true);
      updateOrder(
        filterSkipDays(days, skippedDays),
        selectedOrder.order.OrderId,
        false,
        null,
        recipeIngredientReplacementMode,
        PaymentProcessor.UNKNOWN,
        null,
        handleResponse,
        catchErrors
      );
      return;
    }

    setIsPaymentDialogOpen(true);
    window.scrollTo(0, 0);
  };

  const createOrder = () => {
    const onSuccess = (res) => {
      if (!res.data.success) {
        setSelectedFutureOrder({ order: null });
        setIsErrorState(true);
        setErrorMessage("Failed to create future order");
        setIsLoading(false);
        return;
      }

      setFutureOrderDates(undefined);
      fetchOrders(res.data.futureOrderId);

      setSelectedFutureOrder({ order: null });
    };

    const onError = () => {
      setSelectedFutureOrder({ order: null });
      setIsErrorState(true);
      setErrorMessage("Failed to create future order");
      setIsLoading(false);
    };

    let order = {
      OrderId: selectedOrder.order.OrderId,
      StartDate: selectedFutureOrder.order.StartDate,
      days: filterSkipDays(days, skippedDays),
      RecipeTypeId: selectedOrder.order.RecipeTypeId,
    };

    setIsLoading(true);
    createFutureOrder(order, recipeIngredientReplacementMode, onSuccess, onError);
  };

  const subscriptionContinueCallback = () => {
    setShowSubscriptionWarningDialog(false);
    let callback = subscriptionWarningCallback();
    callback();
  };

  /**
   * Ensures all subscription meals are selected before executing the provided callback function.
   * If subscription meals are incomplete, it triggers a warning dialog.
   *
   * @param {Function} callback - The function to execute after validation. If the subscription warning is triggered,
   *                              this callback will be executed upon confirmation the warning dialog.
   */
  const checkSubscriptionMealsPrehook = (callback) => {
    if (isSubscriptionOrder && selectedMealQuantity < totalMealQuantity) {
      setSubscriptionWarningCallback(() => () => callback);
      setSubscriptionWarningText(myMealsConfig.WarningModalText);
      setSubscriptionWarningTitle(myMealsConfig.WarningModalTitle);
      setShowSubscriptionWarningDialog(true);
    } else {
      callback();
    }
  };

  const saveAndUpdateFutureOrder = () => {
    let saveChangesStatus = proceedWithSaveChanges(
      myMealsConfig,
      filterSkipDays(days, skippedDays),
      totalMealQuantityPerDay,
      selectedOrderMealQuantity,
      amountsPerDay,
      amountForOrder,
      totalQuantitiesPerDay,
      selectedOrder.order.OrderType
    );

    if (!saveChangesStatus.proceed) {
      const deDupedWarningText = deDupeWarningText(saveChangesStatus.dayWarningMessages);
      handleShowErrorModal(deDupedWarningText);
      return;
    }

    //If selected future order is null then were on a current order so update it
    if (selectedFutureOrder.order === null) {
      setIsLoading(true);
      updateOrder(
        days,
        selectedOrder.order.OrderId,
        false,
        null,
        recipeIngredientReplacementMode,
        PaymentProcessor.UNKNOWN,
        null,
        updateDataForOrder,
        catchErrors
      );
    } else {
      createOrder();
    }

    window.scrollTo(0, 0);
  };

  const haveQuantitiesChangedFromOriginal = useCallback(() => {
    // If originalSelectionQuantity is null, the fetch has not completed and we cannot compare.
    if (!days || allDaysPastCutoff || !originalSelectionQuantity) return false;

    return hasQuantityDiff(days, originalSelectionQuantity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, originalSelectionQuantity]);

  useEffect(() => {
    if (myMealsConfig) {
      if (chargeForUpdates && balance?.remainingBalance > 0) {
        setSaveAndUpdateText(myMealsConfig.SaveAndPayButtonText);
      } else {
        setSaveAndUpdateText(myMealsConfig.SaveChangesButtonText);
      }
      setQuickSaveToolTipText(myMealsConfig.QuickSaveToolTipText);
    }
  }, [chargeForUpdates, myMealsConfig, balance]);

  const promptToSave =
    !isPaymentDialogOpen &&
    !isLoading &&
    (haveQuantitiesChangedFromOriginal() || haveRecipesChangedFromOriginal() || haveProductsChangedFromOriginal());

  useEffect(() => {
    const isSelectedDaySkipped =
      selectedDay &&
      skippedDays &&
      skippedDays.find((skipDay) =>
        moment(skipDay.customerDate).utc().isSame(moment(selectedDay.DateFormatted).utc(), "day")
      );

    if (isSelectedDaySkipped) {
      let newDate = getDefaultSelectedDay(days, skippedDays);
      setSelectedDay(newDate);
    }
  }, [selectedDay, skippedDays]);

  // ========== NAVIGATION MENU USE EFFECT ==========
  useEffect(() => {
    let mounted = true;

    // Engage this useEffect only if the location state has an order number and isMyMealsNavMenuSelection is true.
    if (mounted && orderNumber && location?.state?.isMyMealsNavMenuSelection) {
      // Bail out early if the new state is the same as the current state
      if (!isEqual(location.state, navMenuLocationStateData)) {
        setNavMenuLocationStateData(navMenuLocationStateData);

        if (orders && selectedOrder.order.OrderNumber !== orderNumber) {
          const targetOrder = orders.find((order) => order.OrderNumber === orderNumber);
          setSelectedOrder({ order: targetOrder });
        }
      }
    }

    return () => {
      mounted = false;
    };
  }, [location.state]);

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      if (!selectedSubLinkId && selectedOrder && selectedOrder?.order?.SubscriptionLinkId) {
        // Set the selected order's sub link id if it has not already been set.
        setSelectedSubLinkId(selectedOrder.order.SubscriptionLinkId);
        return;
      }

      if (!!selectedSubLinkId && selectedOrder && selectedOrder?.order?.SubscriptionLinkId) {
        // Update the selected order's sub link id if it has changed.
        if (selectedSubLinkId !== selectedOrder.order.SubscriptionLinkId) {
          setSelectedSubLinkId(selectedOrder.order.SubscriptionLinkId);
        }
      }
    }

    return () => {
      mounted = false;
    };
  }, [selectedOrder]);

  const qtyChanges = haveQuantitiesChangedFromOriginal();
  const prodChanges = haveProductsChangedFromOriginal();
  const recipeChanges = haveRecipesChangedFromOriginal();

  useEffect(() => {
    let mounted = true;

    if (mounted && !allDaysPastCutoff) {
      const hasChanges = qtyChanges || prodChanges || recipeChanges;
      const isFutureOrder = selectedFutureOrder && selectedFutureOrder.order;

      if (hasChanges || isFutureOrder) {
        setShowQuickSaveButton(true);
      } else {
        setShowQuickSaveButton(false);
      }
    }

    return () => {
      mounted = false;
    };
  }, [qtyChanges, prodChanges, recipeChanges, allDaysPastCutoff]);

  const isSubscriptionOrder =
    selectedOrder &&
    selectedOrder.order &&
    (selectedOrder.order.SubscriptionStatus === SubscriptionStatus.Active ||
      selectedOrder.order.SubscriptionStatus === SubscriptionStatus.Cancelled ||
      selectedOrder.order.SubscriptionStatus === SubscriptionStatus.Complete);

  let myMealsSectionClass = "col-xs-12";

  // Smaller column to account for meal tabs on desktop
  const displayDesktopTabs =
    !isTabletOrMobile && (!myMealsConfig || (myMealsConfig && myMealsConfig.TabViewForMealTypes));

  // ===== CONDITIONAL RENDERING =====
  const showMealTabs = myMealsConfig && myMealsConfig?.TabViewForMealTypes && selectedDay && !allDaysPastCutoff;
  const showDesktopMealTabs = showMealTabs && !isTabletOrMobile;
  const showMobileMealTabs = showMealTabs && isTabletOrMobile;
  const showMealTabsSkeleton =
    !showMealTabs &&
    !isTabletOrMobile &&
    (!myMealsConfig || myMealsConfig?.TabViewForMealTypes) &&
    !isErrorState &&
    !allDaysPastCutoff;
  const showOrderNumber =
    (selectedOrder?.order?.OrderStatus === ORDER_STATUS.ACTIVE ||
      selectedOrder?.order?.OrderStatus === ORDER_STATUS.FUTURE) &&
    selectedFutureOrder?.order === null &&
    selectedOrder;
  const showMealToEdit = mealToEdit && areDayMealsModifiable(selectedDay?.Config) && isEditMealDialogOpen;
  const showMealDaySection =
    !isErrorState && days && orders && selectedDay && config && !isLoadingFutureOrder && myMealsCompanyText;

  const anyLoading = isLoading || isLoadingFutureOrder || !showMealDaySection;

  return (
    <AccountPageWrapper id="my-meals-page" showFullScreenLoader={anyLoading} loadingText={loadingText}>
      <AccountPageTitle title={MyMealsLinkText ?? "My Meals"} />
      <Prompt when={promptToSave} />
      <Grid container className={`choose-meal-page`}>
        {/* Meal Type Tabs Desktop */}
        <Grid item xs={displayDesktopTabs ? 2 : 0} style={{ paddingRight: "15px" }}>
          {showMealTabsSkeleton && <MealTabsSkeleton />}

          {showDesktopMealTabs && (
            <MealTabs
              day={selectedDay}
              myMealsConfig={myMealsConfig}
              selectedOrderRecipeType={getCurrentRecipeType(selectedOrder, selectedFutureOrder)}
              handleEditMealClick={handleEditMealClick}
            />
          )}
        </Grid>

        {/* My Meals sections */}
        <Grid item xs={displayDesktopTabs ? 10 : 12} className={myMealsSectionClass} style={{ paddingBottom: "70px" }}>
          {showMealDaySection ? (
            <>
              <MealDaySection
                allDaysPastCutoff={allDaysPastCutoff}
                allowFutureOrderCreation={allowFutureOrderCreation}
                cartIcon={cartIcon}
                checkSubscriptionMealsPrehook={checkSubscriptionMealsPrehook}
                companyText={myMealsCompanyText}
                days={days}
                futureOrders={futureOrders}
                getDefaultSelectedDay={getDefaultSelectedDay}
                getFutureOrder={getFutureOrder}
                handleDayChange={setSelectedDay}
                handleOrderChange={(order) => {
                  setAllowPastCutoff(true);
                  setSelectedOrder(order);
                }}
                handleSkipDay={handleSkipDay}
                isFutureOrder={
                  selectedOrder.order.OrderStatus === ORDER_STATUS.FUTURE || selectedFutureOrder.order !== null
                }
                isSubscriptionOrder={isSubscriptionOrder}
                isTabletOrMobile={isTabletOrMobile}
                isUpdatingPrice={isUpdatingPrice || isLoading}
                myMealsConfig={myMealsConfig}
                orders={orders}
                quickSaveToolTipText={quickSaveToolTipText}
                saveAndUpdateCart={saveAndUpdateCart}
                saveAndUpdateFutureOrder={saveAndUpdateFutureOrder}
                saveAndUpdateText={saveAndUpdateText}
                scrollPosition={scrollPosition}
                selectedDay={selectedDay}
                selectedFutureOrder={selectedFutureOrder.order}
                selectedMealQuantity={selectedMealQuantity}
                selectedOrder={selectedOrder.order}
                setDays={setDays}
                setFutureOrders={setFutureOrders}
                setSelectedDay={setSelectedDay}
                setSelectedFutureOrder={setSelectedFutureOrder}
                showQuickSaveButton={showQuickSaveButton}
                skippedDays={skippedDays}
                subtotal={balance?.remainingBalance ?? 0}
                toggleDrawer={toggleDrawer}
                totalMealQuantity={totalMealQuantity}
              />

              {/* Meal Type Tabs Mobile */}
              {showMobileMealTabs && (
                <MealTabs
                  day={selectedDay}
                  myMealsConfig={myMealsConfig}
                  selectedOrderRecipeType={getCurrentRecipeType(selectedOrder, selectedFutureOrder)}
                  handleEditMealClick={handleEditMealClick}
                  scrollPosition={scrollPosition}
                />
              )}

              {!allDaysPastCutoff && (
                <MealRowSection
                  day={selectedDay}
                  days={days}
                  skippedDays={skippedDays}
                  recipeImageBaseUrl={config.RecipeImageBaseUrl}
                  productImageBaseUrl={config.ProductImageBaseUrl}
                  selectedOrderOmissions={selectedOrder.order.Omissions}
                  selectedOrderRecipeType={getCurrentRecipeType(selectedOrder, selectedFutureOrder)}
                  handleSubmit={customizationSubmit}
                  handleCustomizeChange={updateCustomizationQuantity}
                  handleRecipeIncrement={incrementRecipeQuantity}
                  handleRecipeDecrement={decrementRecipeQuantity}
                  handleProductQuantityChange={updateProductQuantity}
                  handleMealQuantityChange={updateMealQuantity}
                  handleRecipeToggle={handleRecipeToggle}
                  handleCustomizeToggle={handleCustomizeToggle}
                  companyText={myMealsCompanyText}
                  culture={culture}
                  myMealsConfig={myMealsConfig}
                  productAndRecipeCardLayoutSettings={productAndRecipeCardLayoutSettings}
                  mealQuantityPerDay={totalMealQuantityPerDay}
                  selectedOrderMealQuantity={selectedOrderMealQuantity}
                  amountsPerDay={amountsPerDay}
                  amountForOrder={amountForOrder}
                  updateAmountsPerDay={setAmountsPerDay}
                  updateAmountForOrder={setAmountForOrder}
                  updateTotalMealQuantity={setTotalMealQuantity}
                  updateMealQuantityPerDay={setTotalMealQuantityPerDay}
                  updateSelectedOrderMealQuantity={setSelectedOrderMealQuantity}
                  updateMealPropertiesPerDay={updateMealPropertiesPerDay}
                  totalQuantitiesPerDay={totalQuantitiesPerDay}
                  updateTotalQuantitiesPerDay={setTotalQuantitiesPerDay}
                  symbolFlags={symbolFlags}
                  mealToEdit={mealToEdit}
                  handleEditMealClick={handleEditMealClick}
                  recipeIngredientReplacementMode={recipeIngredientReplacementMode}
                  alwaysPromptCustomizeMealModal={config.GeneralSettings.AlwaysPromptCustomizeMealModal}
                />
              )}

              {showOrderNumber && (
                <Box mt={2}>
                  <Typography variant="body3" style={{ color: "grey" }}>
                    Order #{selectedOrder.order.OrderNumber}
                  </Typography>
                </Box>
              )}

              {showMealToEdit && (
                <EditMealDialog
                  dayOfWeek={moment(selectedDay.DateFormatted).format("dddd")}
                  isOpen={isEditMealDialogOpen}
                  onClose={() => handleEditMealDialogClose(days)}
                  existingQuantity={mealToEdit.Quantity}
                  mealName={mealToEdit.Name}
                  recipes={selectedDay.Recipes.filter((r) => r.ProgramMealId === mealToEdit.ProgramMealId)}
                  companyText={myMealsCompanyText}
                  setIsOpen={setIsEditMealDialogOpen}
                  handleMealQuantityChange={updateMealQuantity}
                  handleRecipeQuantityChange={updateRecipeQuantity}
                  programMealId={mealToEdit.ProgramMealId}
                  addMealsEnabled={selectedDay.Config.CanAddMeals}
                  removeMealsEnabled={selectedDay.Config.CanRemoveMeals}
                  myMealsConfig={myMealsConfig}
                  day={selectedDay}
                  days={filterSkipDays(days, skippedDays)}
                  mealQuantityPerDay={totalMealQuantityPerDay}
                  selectedOrderMealQuantity={selectedOrderMealQuantity}
                  amountsPerDay={amountsPerDay}
                  amountForOrder={amountForOrder}
                  updateAmountsPerDay={setAmountForOrder}
                  updateAmountForOrder={setAmountForOrder}
                  updateSelectedOrderMealQuantity={setSelectedOrderMealQuantity}
                  updateMealQuantityPerDay={setTotalMealQuantityPerDay}
                  totalQuantitiesPerDay={totalQuantitiesPerDay}
                  updateTotalQuantitiesPerDay={setTotalQuantitiesPerDay}
                  orderType={selectedOrder.order.OrderType}
                />
              )}
            </>
          ) : (
            !isErrorState && (
              <>
                <MyMealsSkeleton />
              </>
            )
          )}

          {/*
            By conditionally displaying these elements/modals, we prevent them from being rendered on page load, 
            which then prevents their logic from being executed until they are actually shown. 
          */}
          {(isErrorState || skipDaysError) && (
            <Box style={{ fontSize: "large" }} className="centered-box">
              {isErrorState && errorMessage}
              {skipDaysError && skipDaysError}
            </Box>
          )}

          {selectedOrder && selectedDay && isDrawerOpen && (
            <CartDrawer
              isDrawerOpen={isDrawerOpen}
              toggleDrawer={toggleDrawer}
              cartSettings={convertToCartVisibilitySettings(cartSettings, config.SkipPayment, true)}
              selectedOrder={selectedOrder.order}
              selectedFutureOrder={selectedFutureOrder.order}
              days={filterSkipDays(days, skippedDays)}
              myMealsConfig={myMealsConfig}
              selectedOrderRecipeType={getCurrentRecipeType(selectedOrder, selectedFutureOrder)}
              balance={balance}
              saveButtonText={saveAndUpdateText}
              loading={isUpdatingPrice || isLoading}
              setIsLoading={setIsLoading}
              haveQuantitiesChanged={
                haveQuantitiesChangedFromOriginal() ||
                haveProductsChangedFromOriginal() ||
                haveRecipesChangedFromOriginal()
              }
              saveAndUpdateFutureOrder={saveAndUpdateFutureOrder}
              checkSubscriptionMealsPrehook={checkSubscriptionMealsPrehook}
              isFutureOrder={
                selectedOrder.order.OrderStatus === ORDER_STATUS.FUTURE || selectedFutureOrder.order !== null
              }
              selectedOrderMealQuantity={selectedOrderMealQuantity}
              totalMealQuantityPerDay={totalMealQuantityPerDay}
              amountsPerDay={amountsPerDay}
              amountForOrder={amountForOrder}
              totalQuantitiesPerDay={totalQuantitiesPerDay}
              skipPayment={config ? config.SkipPayment : false}
              handleShowErrorModal={handleShowErrorModal}
              setIsPaymentDialogOpen={setIsPaymentDialogOpen}
              recipeIngredientReplacementMode={recipeIngredientReplacementMode}
              onComplete={updateDataForOrder}
            />
          )}
        </Grid>
      </Grid>

      {isPaymentDialogOpen && (
        <PaymentDialog
          handlePaymentDialogClose={() => setIsPaymentDialogOpen(false)}
          isPaymentDialogOpen={isPaymentDialogOpen}
          days={days}
          orderId={selectedOrder.order.OrderId}
          onPaymentComplete={() => {
            updateDataForOrder();
            setIsPaymentDialogOpen(false);
          }}
          recipeIngredientReplacementMode={recipeIngredientReplacementMode}
          selectedOrder={selectedOrder.order}
          myMealsConfig={myMealsConfig}
        />
      )}

      {isFutureOrderDialogOpen && (
        <FutureOrderDialog
          isDialogOpen={isFutureOrderDialogOpen}
          onClick={() => setIsFutureOrderDialogOpen(false)}
          text={futureOrderDialogText}
        />
      )}

      {showErrorDialog && (
        <Dialog
          open={showErrorDialog}
          onClose={() => {
            setShowErrorDialog(false);
          }}
        >
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="h3" color="primary">
                {myMealsConfig ? myMealsConfig.ErrorTitle : "Your changes do not meet the requirement."}
              </Typography>
              <ExtraSmallFab icon={CloseIcon} aria-label="close" onClick={() => setShowErrorDialog(false)} />
            </Stack>
          </DialogTitle>

          <DialogContent>
            <Stack spacing={1}>
              {warningText && warningText.map((warning) => <Typography key={createKey()}>{warning}</Typography>)}
            </Stack>
          </DialogContent>
        </Dialog>
      )}

      {showSubscriptionWarningDialog && (
        <Dialog
          open={showSubscriptionWarningDialog}
          onClose={() => {
            setShowSubscriptionWarningDialog(false);
          }}
        >
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h3" color="primary">
                {subscriptionWarningTitle}
              </Typography>
              <ExtraSmallFab
                icon={CloseIcon}
                aria-label="close"
                onClick={() => setShowSubscriptionWarningDialog(false)}
              />
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack>
              <Typography>{subscriptionWarningText}</Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="primary" onClick={() => setShowSubscriptionWarningDialog(false)}>
              Back
            </Button>
            <Button variant="contained" onClick={subscriptionContinueCallback}>
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </AccountPageWrapper>
  );
};

export default MyMealsPage;

function useCalculationDebounce(days, setIsUpdatingPrice, shouldSetLoadingState, delay) {
  const [debouncedDays, setDebouncedDays] = useState(days);
  useEffect(() => {
    if (shouldSetLoadingState) {
      setIsUpdatingPrice(true);
    }
    const handler = setTimeout(() => {
      setDebouncedDays(days);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [days, setIsUpdatingPrice, shouldSetLoadingState, delay]);

  return debouncedDays;
}
